import type {
  UseRadioGroupProps,
  UseRadioGroupReturn
} from '@chakra-ui/react'
import {
  FormErrorMessage,
  useRadioGroup
} from '@chakra-ui/react'
import { createContext } from '@chakra-ui/react-utils'
import type { HTMLChakraProps, ThemingProps } from '@chakra-ui/system'
import { chakra, forwardRef } from '@chakra-ui/system'
import { __DEV__, cx } from '@chakra-ui/utils'
import type { Control } from 'BrokerTsLib/src/rhf'
import { useController } from 'BrokerTsLib/src/rhf'
import { useMemo } from 'react'

export interface RadioGroupContext
  extends Pick<UseRadioGroupReturn, 'onChange' | 'value' | 'name' | 'isDisabled' | 'isFocusable'>,
  Omit<ThemingProps<'Radio'>, 'orientation'> { }

const [RadioGroupProvider, useRadioGroupContext] = createContext<RadioGroupContext>({
  name: 'RadioGroupContext',
  strict: false,
})

export { useRadioGroupContext }

type Omitted = 'onChange' | 'value' | 'defaultValue' | 'defaultChecked' | 'children' | 'name'
export interface RadioGroupProps
  extends UseRadioGroupProps,
  Omit<HTMLChakraProps<'div'>, Omitted>,
  Omit<ThemingProps<'Radio'>, 'orientation'> {
  children: React.ReactNode
  control: Control<any>
  name: string
}

/**
 * Used for multiple radios which are bound in one group,
 * and it indicates which option is selected.
 *
 * @see Docs https://chakra-ui.com/radio
 */
export const RadioGroup = forwardRef<RadioGroupProps, 'div'>((props, ref) => {
  const {
    colorScheme,
    size,
    value,
    defaultValue,
    variant,
    children,
    name,
    className,
    control,
    isDisabled,
    isFocusable,
    ...rest
  } = props


  const {
    field,
    formState: { errors },
  } = useController({
    control,
    name,
  })

  const { getRootProps, htmlProps } = useRadioGroup({
    onChange: field.onChange,
    value: field.value,
    name,
    ...rest,
  })

  const group = useMemo(
    () => ({
      name,
      size,
      onChange: field.onChange,
      colorScheme,
      value: value ?? field.value?.toString(),
      defaultValue: defaultValue ?? field.value?.toString(),
      variant,
      isDisabled,
      isFocusable,
    }),
    [name, size, value, defaultValue, field.onChange, colorScheme, field.value, variant, isDisabled, isFocusable],
  )

  const groupProps = getRootProps(htmlProps, ref)
  const _className = cx('chakra-radio-group', className)
  const errorMessage = errors && errors[name] && errors[name]?.message

  return (
    <RadioGroupProvider value={group} {...groupProps} >
      <chakra.div className={_className}>
        {children}
      </chakra.div>
      {errorMessage && <FormErrorMessage>{errorMessage as string}</FormErrorMessage>}
    </RadioGroupProvider>
  )
})

if (__DEV__) {
  RadioGroup.displayName = 'RadioGroup'
}
